"use client"

import React from "react"
import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { usersAPI, restaurantOwnerAPI } from "../services/api"
import { Eye, Check, X, User, Building } from "lucide-react"
import toast from "react-hot-toast"

const UsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"users" | "restaurant-owners">("users")
  const [filters, setFilters] = useState({
    role: "",
    isActive: "",
    page: 1,
    limit: 20,
  })
  const [restaurantOwnerFilters, setRestaurantOwnerFilters] = useState({
    isActive: "",
    isVerified: "",
    page: 1,
    limit: 20,
  })

  const queryClient = useQueryClient()

  // Users query
  const { data: usersData, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ["users", filters],
    queryFn: () => usersAPI.getUsers(filters),
    enabled: activeTab === "users",
  })

  // Restaurant owners query
  const { data: restaurantOwnersData, isLoading: restaurantOwnersLoading, error: restaurantOwnersError } = useQuery({
    queryKey: ["restaurant-owners", restaurantOwnerFilters],
    queryFn: () => restaurantOwnerAPI.getRestaurantOwners(restaurantOwnerFilters),
    enabled: activeTab === "restaurant-owners",
  })

  // User mutations
  const toggleStatusMutation = useMutation({
    mutationFn: (userId: string) => usersAPI.toggleStatus(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      toast.success("User status updated successfully")
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update user status")
    },
  })

  // Restaurant owner mutations
  const approveRestaurantOwnerMutation = useMutation({
    mutationFn: (ownerId: string) => restaurantOwnerAPI.approveRestaurantOwner(ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant-owners"] })
      toast.success("Restaurant owner approved successfully")
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to approve restaurant owner")
    },
  })

  const rejectRestaurantOwnerMutation = useMutation({
    mutationFn: (ownerId: string) => restaurantOwnerAPI.rejectRestaurantOwner(ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant-owners"] })
      toast.success("Restaurant owner rejected successfully")
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to reject restaurant owner")
    },
  })

  const toggleRestaurantOwnerStatusMutation = useMutation({
    mutationFn: (ownerId: string) => restaurantOwnerAPI.toggleRestaurantOwnerStatus(ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant-owners"] })
      toast.success("Restaurant owner status updated successfully")
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update restaurant owner status")
    },
  })

  const users = usersData?.data?.users || []
  const restaurantOwners = restaurantOwnersData?.data?.restaurantOwners || []
  const pagination = usersData?.data?.pagination || {}
  const restaurantOwnerPagination = restaurantOwnersData?.data?.pagination || {}

  const handleToggleStatus = (userId: string) => {
    toggleStatusMutation.mutate(userId)
  }

  const handleApproveRestaurantOwner = (ownerId: string) => {
    approveRestaurantOwnerMutation.mutate(ownerId)
  }

  const handleRejectRestaurantOwner = (ownerId: string) => {
    rejectRestaurantOwnerMutation.mutate(ownerId)
  }

  const handleToggleRestaurantOwnerStatus = (ownerId: string) => {
    toggleRestaurantOwnerStatusMutation.mutate(ownerId)
  }

  // Helper function to safely get image URL
  const getImageUrl = (profileImage: string | null) => {
    if (!profileImage) return null;
    
    // Check if it's a local file path (which won't work in web browsers)
    if (profileImage.startsWith('file://') || profileImage.startsWith('/Users/') || profileImage.startsWith('/data/')) {
      return null; // Return null for local file paths
    }
    
    // Return the image URL if it's a valid web URL
    return profileImage;
  };

  const isLoading = activeTab === "users" ? usersLoading : restaurantOwnersLoading
  const error = activeTab === "users" ? usersError : restaurantOwnersError

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 text-lg">Error loading data</p>
          <p className="text-gray-500 text-sm mt-2">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage platform users and restaurant owners
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("users")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "users"
                ? "border-red-500 text-red-600 dark:text-red-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>Users ({users.length})</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab("restaurant-owners")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "restaurant-owners"
                ? "border-red-500 text-red-600 dark:text-red-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            <div className="flex items-center space-x-2">
              <Building className="h-5 w-5" />
              <span>Restaurant Owners ({restaurantOwners.length})</span>
            </div>
          </button>
        </nav>
      </div>

      {activeTab === "users" && (
        <>
          {/* User Filters */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                <select
                  value={filters.role}
                  onChange={(e) => setFilters({ ...filters, role: e.target.value, page: 1 })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Roles</option>
                  <option value="customer">Customer</option>
                  <option value="captain">Captain</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                <select
                  value={filters.isActive}
                  onChange={(e) => setFilters({ ...filters, isActive: e.target.value, page: 1 })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Orders
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total Spent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user: any) => {
                      const imageUrl = getImageUrl(user.profileImage);
                      return (
                        <tr key={user._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                {imageUrl ? (
                                  <img
                                    className="h-10 w-10 rounded-full object-cover"
                                    src={imageUrl}
                                    alt={user.username}
                                    onError={(e) => {
                                      // Fallback to initials if image fails to load
                                      e.currentTarget.style.display = 'none';
                                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : null}
                                <div className={`h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center ${imageUrl ? 'hidden' : ''}`}>
                                  <span className="text-sm font-medium text-gray-700">
                                    {user.username?.charAt(0).toUpperCase() || 'U'}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{user.username || 'Unknown User'}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">{user.phoneNumber || 'No phone'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.role === "admin"
                                  ? "bg-purple-100 text-purple-800"
                                  : user.role === "captain"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-green-100 text-green-800"
                              }`}
                            >
                              {user.role || 'customer'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                              }`}
                            >
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {user.stats?.totalOrders || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">SDG {user.stats?.totalSpent?.toFixed(2) || "0.00"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-900 dark:text-blue-400">
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleToggleStatus(user._id)}
                                className={`px-2 py-1 text-xs rounded ${
                                  user.isActive
                                    ? "bg-red-100 text-red-800 hover:bg-red-200"
                                    : "bg-green-100 text-green-800 hover:bg-green-200"
                                }`}
                              >
                                {user.isActive ? "Deactivate" : "Activate"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {(pagination.currentPage - 1) * filters.limit + 1} to{" "}
                    {Math.min(pagination.currentPage * filters.limit, pagination.totalUsers)} of {pagination.totalUsers} results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setFilters({ ...filters, page: pagination.currentPage - 1 })}
                      disabled={!pagination.hasPrevPage}
                      className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setFilters({ ...filters, page: pagination.currentPage + 1 })}
                      disabled={!pagination.hasNextPage}
                      className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "restaurant-owners" && (
        <>
          {/* Restaurant Owner Filters */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                <select
                  value={restaurantOwnerFilters.isActive}
                  onChange={(e) => setRestaurantOwnerFilters({ ...restaurantOwnerFilters, isActive: e.target.value, page: 1 })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Verification</label>
                <select
                  value={restaurantOwnerFilters.isVerified}
                  onChange={(e) => setRestaurantOwnerFilters({ ...restaurantOwnerFilters, isVerified: e.target.value, page: 1 })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Verification</option>
                  <option value="true">Verified</option>
                  <option value="false">Pending</option>
                </select>
              </div>
            </div>
          </div>

          {/* Restaurant Owners Table */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Restaurant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Verification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {restaurantOwners.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No restaurant owners found
                      </td>
                    </tr>
                  ) : (
                    restaurantOwners.map((owner: any) => (
                      <tr key={owner._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-blue-700">
                                  {owner.name?.charAt(0).toUpperCase() || 'O'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{owner.name || 'Unknown Owner'}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{owner.email || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {owner.restaurant?.name || 'Unknown Restaurant'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {owner.restaurant?.address ? 
                              (typeof owner.restaurant.address === 'string' 
                                ? owner.restaurant.address 
                                : `${owner.restaurant.address.street || ''}, ${owner.restaurant.address.city || ''}, ${owner.restaurant.address.state || ''} ${owner.restaurant.address.zipCode || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '')
                              ) 
                              : 'No address'
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              owner.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            }`}
                          >
                            {owner.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              owner.isVerified ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {owner.isVerified ? "Verified" : "Pending"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {owner.role || 'owner'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {owner.createdAt ? new Date(owner.createdAt).toLocaleDateString() : 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button className="text-blue-600 hover:text-blue-900 dark:text-blue-400">
                              <Eye className="h-4 w-4" />
                            </button>
                            {!owner.isVerified && (
                              <>
                                <button
                                  onClick={() => handleApproveRestaurantOwner(owner._id)}
                                  className="text-green-600 hover:text-green-900 dark:text-green-400"
                                  title="Approve"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleRejectRestaurantOwner(owner._id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400"
                                  title="Reject"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleToggleRestaurantOwnerStatus(owner._id)}
                              className={`px-2 py-1 text-xs rounded ${
                                owner.isActive
                                  ? "bg-red-100 text-red-800 hover:bg-red-200"
                                  : "bg-green-100 text-green-800 hover:bg-green-200"
                              }`}
                            >
                              {owner.isActive ? "Deactivate" : "Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {restaurantOwnerPagination.totalPages > 1 && (
              <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {(restaurantOwnerPagination.currentPage - 1) * restaurantOwnerFilters.limit + 1} to{" "}
                    {Math.min(restaurantOwnerPagination.currentPage * restaurantOwnerFilters.limit, restaurantOwnerPagination.totalRestaurantOwners)} of {restaurantOwnerPagination.totalRestaurantOwners} results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setRestaurantOwnerFilters({ ...restaurantOwnerFilters, page: restaurantOwnerPagination.currentPage - 1 })}
                      disabled={!restaurantOwnerPagination.hasPrevPage}
                      className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setRestaurantOwnerFilters({ ...restaurantOwnerFilters, page: restaurantOwnerPagination.currentPage + 1 })}
                      disabled={!restaurantOwnerPagination.hasNextPage}
                      className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default UsersPage
